import http from './http'
import type { User, UserWithRole, UserStats, InviteUserData, UserSettings, PendingInvitation, APIUserResponse } from '../types/user'

/**
 * User Service
 * Handles all user and team management API calls
 *
 * Uses real B2B backend endpoints at /api/b2b/users/*
 */

class UserService {
  /**
   * Get all team members (admin only)
   * GET /api/b2b/users/organization
   * Returns the raw backend response format (snake_case)
   */
  async getUsers(): Promise<APIUserResponse[]> {
    console.log('[UserService.getUsers] Fetching organization users')
    console.log('[UserService.getUsers] API URL:', http.defaults.baseURL)
    try {
      const response = await http.get<APIUserResponse[]>('/api/b2b/users/organization')
      console.log('[UserService.getUsers] Response status:', response.status)
      console.log('[UserService.getUsers] Fetched', response.data?.length || 0, 'users')
      console.log('[UserService.getUsers] Raw data:', JSON.stringify(response.data, null, 2))
      return response.data || []
    } catch (error: any) {
      console.error('[UserService.getUsers] Failed:', error)
      console.error('[UserService.getUsers] Error response:', error.response?.data)
      console.error('[UserService.getUsers] Error status:', error.response?.status)
      throw this.handleError(error)
    }
  }

  /**
   * Get current user's profile
   * GET /api/b2b/users/me
   */
  async getCurrentUser(): Promise<UserWithRole> {
    console.log('[UserService.getCurrentUser] Fetching current user')
    try {
      const response = await http.get<UserWithRole>('/api/b2b/users/me')
      console.log('[UserService.getCurrentUser] Fetched:', response.data.email)
      return response.data
    } catch (error: any) {
      console.error('[UserService.getCurrentUser] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Get a single user by ID
   * For own user: GET /api/b2b/users/me
   * For other users: Look up in organization users
   */
  async getUserById(userId: string): Promise<UserWithRole> {
    console.log('[UserService.getUserById] Fetching user:', userId)
    try {
      // First try to get the user from organization list
      const response = await http.get<UserWithRole[]>('/api/b2b/users/organization')
      const user = response.data.find(u => u.id === userId)
      if (user) {
        return user
      }
      // If not found, it might be the current user
      const meResponse = await http.get<UserWithRole>('/api/b2b/users/me')
      if (meResponse.data.id === userId) {
        return meResponse.data
      }
      throw new Error('User not found')
    } catch (error: any) {
      console.error('[UserService.getUserById] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Get user statistics
   * GET /api/b2b/analytics/users/{id}
   */
  async getUserStats(userId: string): Promise<UserStats> {
    console.log('[UserService.getUserStats] Fetching stats for:', userId)
    try {
      const response = await http.get<any>(`/api/b2b/analytics/users/me`)
      // Transform analytics response to UserStats format
      const analytics = response.data
      return {
        posts: analytics.total_posts || 0,
        reactions: analytics.total_likes || 0,
        comments: analytics.total_comments || 0,
        reshares: analytics.total_shares || 0,
        impressions: analytics.total_impressions || 0,
        total_posts: analytics.total_posts || 0,
        published_posts: analytics.published_posts || 0,
        draft_posts: analytics.draft_posts || 0,
        total_engagement: (analytics.total_likes || 0) + (analytics.total_comments || 0) + (analytics.total_shares || 0),
        avg_engagement_rate: analytics.avg_engagement_rate || 0,
        top_performing_post_id: analytics.top_post_id
      }
    } catch (error: any) {
      console.error('[UserService.getUserStats] Failed:', error)
      // Return empty stats if analytics not available
      return {
        posts: 0,
        reactions: 0,
        comments: 0,
        reshares: 0,
        impressions: 0,
        total_posts: 0,
        published_posts: 0,
        draft_posts: 0,
        total_engagement: 0,
        avg_engagement_rate: 0
      }
    }
  }

  /**
   * Invite a new team member
   * POST /api/b2b/activity/invitations
   */
  async inviteUser(data: InviteUserData): Promise<PendingInvitation> {
    console.log('[UserService.inviteUser] Inviting:', data.email)
    try {
      const response = await http.post<any>('/api/b2b/activity/invitations', {
        email: data.email,
        first_name: data.firstName,
        last_name: data.lastName,
        role: data.role,
        department_id: data.department_id,
        expires_days: 7
      })
      // Transform response to PendingInvitation format
      return {
        id: response.data.id,
        email: response.data.email,
        role: response.data.role,
        department_id: response.data.department_id,
        invited_by: response.data.invited_by,
        invited_at: response.data.created_at,
        expires_at: response.data.expires_at,
        status: response.data.accepted ? 'accepted' : 'pending'
      }
    } catch (error: any) {
      console.error('[UserService.inviteUser] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Update user profile
   * PUT /api/b2b/users/{id}
   */
  async updateUser(userId: string, data: Partial<User>): Promise<UserWithRole> {
    console.log('[UserService.updateUser] Updating user:', userId)
    try {
      const response = await http.put<UserWithRole>(`/api/b2b/users/${userId}`, data)
      console.log('[UserService.updateUser] Updated successfully')
      return response.data
    } catch (error: any) {
      console.error('[UserService.updateUser] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Update current user's profile
   * PUT /api/b2b/users/me
   */
  async updateCurrentUser(data: Partial<User>): Promise<UserWithRole> {
    console.log('[UserService.updateCurrentUser] Updating current user')
    try {
      const response = await http.put<UserWithRole>('/api/b2b/users/me', data)
      console.log('[UserService.updateCurrentUser] Updated successfully')
      return response.data
    } catch (error: any) {
      console.error('[UserService.updateCurrentUser] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Remove a team member
   * Note: This may not be implemented in the B2B backend yet
   */
  async removeUser(userId: string): Promise<void> {
    console.log('[UserService.removeUser] Removing user:', userId)
    try {
      await http.delete(`/api/b2b/users/${userId}`)
    } catch (error: any) {
      console.error('[UserService.removeUser] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Get user settings
   * Note: May need to be stored in user metadata or separate table
   */
  async getUserSettings(userId: string): Promise<UserSettings> {
    console.log('[UserService.getUserSettings] Fetching settings for:', userId)
    // Default settings - can be extended when backend supports user settings
    return {
      notifications_enabled: true,
      email_notifications: true,
      weekly_digest: true,
      auto_publish: false,
      default_visibility: 'public'
    }
  }

  /**
   * Update user settings
   * Note: May need to be stored in user metadata or separate table
   */
  async updateUserSettings(userId: string, settings: Partial<UserSettings>): Promise<UserSettings> {
    console.log('[UserService.updateUserSettings] Updating settings for:', userId)
    // Return merged settings - can be extended when backend supports user settings
    return {
      notifications_enabled: true,
      email_notifications: true,
      weekly_digest: true,
      auto_publish: false,
      default_visibility: 'public',
      ...settings
    }
  }

  /**
   * Get pending invitations
   * GET /api/b2b/activity/invitations
   */
  async getPendingInvitations(): Promise<PendingInvitation[]> {
    console.log('[UserService.getPendingInvitations] Fetching invitations')
    try {
      const response = await http.get<any[]>('/api/b2b/activity/invitations', {
        params: { pending_only: true }
      })
      // Transform response to PendingInvitation format
      return response.data.map(inv => ({
        id: inv.id,
        email: inv.email,
        role: inv.role,
        department_id: inv.department_id,
        invited_by: inv.invited_by,
        invited_at: inv.created_at,
        expires_at: inv.expires_at,
        status: inv.accepted ? 'accepted' : 'pending'
      }))
    } catch (error: any) {
      console.error('[UserService.getPendingInvitations] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Resend a pending invitation email
   * POST /api/b2b/activity/invitations/{id}/resend
   */
  async resendInvitation(invitationId: string): Promise<{ success: boolean; message: string }> {
    console.log('[UserService.resendInvitation] Resending invitation:', invitationId)
    try {
      const response = await http.post<{ success: boolean; message: string }>(
        `/api/b2b/activity/invitations/${invitationId}/resend`
      )
      console.log('[UserService.resendInvitation] Resent successfully')
      return response.data
    } catch (error: any) {
      console.error('[UserService.resendInvitation] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Send a custom reminder email to a team member
   * POST /api/b2b/users/{userId}/remind
   */
  async sendTeamMemberReminder(
    userId: string,
    data: { subject: string; message: string }
  ): Promise<{ success: boolean; message: string }> {
    console.log('[UserService.sendTeamMemberReminder] Sending reminder to:', userId)
    try {
      const response = await http.post<{ success: boolean; message: string }>(
        `/api/b2b/users/${userId}/remind`,
        data
      )
      console.log('[UserService.sendTeamMemberReminder] Sent successfully')
      return response.data
    } catch (error: any) {
      console.error('[UserService.sendTeamMemberReminder] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Cancel a pending invitation
   * DELETE /api/b2b/activity/invitations/{id}
   */
  async cancelInvitation(invitationId: string): Promise<void> {
    console.log('[UserService.cancelInvitation] Cancelling:', invitationId)
    try {
      await http.delete(`/api/b2b/activity/invitations/${invitationId}`)
      console.log('[UserService.cancelInvitation] Cancelled successfully')
    } catch (error: any) {
      console.error('[UserService.cancelInvitation] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Get all editors in the organization
   * GET /api/b2b/users/organization/editors
   */
  async getOrganizationEditors(): Promise<UserWithRole[]> {
    console.log('[UserService.getOrganizationEditors] Fetching editors')
    try {
      const response = await http.get<UserWithRole[]>('/api/b2b/users/organization/editors')
      return response.data
    } catch (error: any) {
      console.error('[UserService.getOrganizationEditors] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Assign an editor to a profile user (admin only)
   * POST /api/b2b/users/{user_id}/assign-editor
   */
  async assignEditor(userId: string, editorId: string): Promise<UserWithRole> {
    console.log('[UserService.assignEditor] Assigning editor', editorId, 'to user', userId)
    try {
      const response = await http.post<UserWithRole>(`/api/b2b/users/${userId}/assign-editor`, {
        editor_id: editorId
      })
      return response.data
    } catch (error: any) {
      console.error('[UserService.assignEditor] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Error handler
   */
  private handleError(error: any): Error {
    if (error.response) {
      const message = error.response.data?.message || error.response.data?.detail || error.response.data?.error || 'An error occurred'
      const customError = new Error(message)
      ;(customError as any).status = error.response.status
      ;(customError as any).data = error.response.data
      return customError
    } else if (error.request) {
      return new Error('Network error. Please check your connection.')
    } else {
      return new Error(error.message || 'An unexpected error occurred')
    }
  }
}

export default new UserService()
